import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import useAlert from "./useAlert.js";
import { useTranslation } from "react-i18next";
import jwt_decode from "jwt-decode";
import queryString from "query-string";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as Action from "@mywvglobal-shared/mywv-shared/action/loginAction";
import AppConstants from "@mywvglobal-shared/mywv-shared/common/appConstants";
import * as PersistItemsAction from "@mywvglobal-shared/mywv-shared/action/persistItemsAction";
import { constants } from "../constants/index.js";
import * as DateUtilities from "../utilities/dateUtilities";
import * as CryptoJS from "crypto-js"
import { Auth0Client } from "@auth0/auth0-spa-js";
import { getCookie } from "../utilities/cookieUtilities.js";
import { checkSSOStatus } from "../utilities/cookieUtilities.js";
import {SELECTED_SO_CODE_JPNO } from "../constants/soCode";


const useLogin = () => {
  const { persistItemsReducer } = useSelector((state) => state);
  const { loginReducer } = useSelector((state) => state);
  const { domainName, clientId,
    provider, refreshTokenIntervalInMinutes,
    supporterID, bearerTokenName } = loginReducer?.getSOConfiguration?.loginModule || {};
  const [active, setActive] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const secretKey = process.env.REACT_APP_SECRET_KEY;
  const subdirectory = (window.location.origin.includes(".nz") ||
    window.location.origin.includes(".at") ||
    window.location.origin.includes(".fi")) ? "" : persistItemsReducer?.selectedSOData?.soCode?.toLowerCase();



  /**
   * Retrieves and decrypts the stored token response from localStorage.
   *
   * @returns {Object|null} The decrypted token response object if available, otherwise null.
   */
  const getTokenResp = () => {
    if (localStorage.getItem("tokenResp") && localStorage.getItem("tokenResp") !== "") {
      const bytes = CryptoJS.AES.decrypt(localStorage.getItem("tokenResp"), secretKey);
      var decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      return decryptedData;
    }
    return null;
  };

  const getAccessToken = () => {
    const resp = getTokenResp();
    return resp?.access_token;
  }
  const getIdToken = () => {
    const resp = getTokenResp();
    return resp?.id_token;
  }

  const getRefreshToken = () => {
    const resp = getTokenResp();
    return resp?.refresh_token;
  }

  const removeAccessToken = () => { localStorage.removeItem("tokenResp"); setAccessToken("") };
  const removeIdToken = () => { localStorage.removeItem("tokenResp"); setIdToken("") };
  const [searchParams] = useSearchParams();
  const [accessToken, setAccessToken] = useState(getAccessToken());
  const [idToken, setIdToken] = useState(getIdToken());
  const [tokenFlag, setTokenFlag] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const getSOConfigurations = useCallback(({
    baseURL = process.env.REACT_APP_BASE_URL,
    appVersion = "28",
    soCode = persistItemsReducer?.selectedSOData?.soCode,
    deviceType = "Web",
    languageCode = persistItemsReducer?.selectedSOData?.soLanguageCode, } = {}) => {
    const network = window.navigator.onLine;
    if (network) {
      dispatch(Action.clearLoginReducer());
      const param = {
        baseURL,
        appVersion,
        soCode,
        deviceType,
        languageCode,
      }
      dispatch(Action.getSOConfigurationRequest(param));
    } else {
      setAlert(t("Warning"), t("NoInternet"), "", "", "error", false, false, false)
    }
  }, [persistItemsReducer?.selectedSOData]);

  /**
   * Sends a POST request to the specified URI with the provided form data to obtain a login token.
   *
   * @async
   * @function
   * @param {string} uri - The endpoint URI to send the POST request to.
   * @param {Object} form - The form data to be sent in the request body.
   * @returns {Promise<Object>} The response from the server containing the login token.
   */
  const getOneLoginToken = async (uri, form) => {
    return await axios.post(
      uri,
      form,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Access-Control-Allow-Origin": "*"
        },
        crossDomain: true
      }
    );
  };


  /**
   * Asynchronously retrieves an Auth0 access token using silent authentication.
   *
   * @async
   * @function getAuth0Token
   * @param {Object} [options={}] - Configuration options for Auth0.
   * @param {string} [options.domain="account-dev.worldvision.org.nz"] - The Auth0 domain.
   * @param {string} [options.clientId="G5w8ZMzHpLZIxKK7XU1PPzx1QpQKxtkF"] - The Auth0 client ID.
   * @returns {Promise<{ data: Object }>} An object containing the authentication token data.
   */
  const getAuth0Token = async ({
    domain = "account.worldvision.org.nz",
    clientId = "Gkh7Zbwlt5cvSluM0X3VvfGTVoR50dUt"
  } = {}) => {
    const auth0 = new Auth0Client({
      domain,
      clientId,
      authorizationParams: {
        redirect_uri: constants?.currentOrigin,
        audience: "https://api.experienceportal.worldvision.org.nz/",
        scope: "openid params profile email offline_access",
      }
    });
    const data = await auth0.getTokenSilently({ detailedResponse: true });
    return { data };
  };

  /**
 * Stores the token response securely and updates token state.
 * @param {object} tokenData - The token response object (should have .data property).
 */
  const handleTokenResponse = (tokenData) => {
    if (tokenData && tokenData.data) {
      const cipherText = CryptoJS.AES.encrypt(JSON.stringify(tokenData.data), secretKey).toString();
      localStorage.setItem("tokenResp", cipherText);
      setTokenFlag(1);
      setTimeout(() => { setTokenFlag(0); }, 5000);
    } else {
      const access_token = getAccessToken();
      if (access_token) setAccessToken(access_token);
      setIdToken(getIdToken());
    }
  };


  /**
   * Retrieves and stores an authentication token based on the login provider configuration.
   * 
   * - For OneLogin, requests a token using provided URI and request parameters.
   * - For Auth0, requests a token using the Auth0 flow.
   * - Encrypts and stores the token in localStorage if successful.
   * - Handles session expiration and logout redirection for both providers.
   * - Manages token state flags to prevent duplicate requests.
   * 
   * @function
   * @async
   * @param {string} uri - The endpoint URI for token retrieval (used for OneLogin).
   * @param {Object} req_params - The request parameters for token retrieval.
   * @returns {Promise<void>}
   */
  const getToken = useCallback(async (uri, req_params) => {
    if (uri && uri !== "" && tokenFlag === 1) return;
    let form = queryString.stringify(req_params);
    let res = {};
    try {
      if (loginReducer?.getSOConfiguration?.loginModule?.provider === AppConstants.OneLogin) {
        res = await getOneLoginToken(uri, form);
      }

      if (loginReducer?.getSOConfiguration?.loginModule?.provider === AppConstants.Auth0) {
   res = await getAuth0Token({domain:loginReducer?.getSOConfiguration?.loginModule.domainName,
         clientId: loginReducer?.getSOConfiguration?.loginModule.clientId});
      }

      handleTokenResponse(res);
    } catch (excep) {
      const logoutUrl = constants?.postLogoutUrl;
      if (excep && excep?.response?.data && excep?.response?.data?.error) {
        const idToken = getIdToken();
        const decoded = idToken ? jwt_decode(idToken) : {};
        const domainName = loginReducer?.getSOConfiguration?.loginModule?.domainName
          ? loginReducer?.getSOConfiguration?.loginModule?.domainName
          : decoded.iss;
        setTimeout(() => {
          removeAccessToken();
          removeIdToken();
          localStorage.removeItem("ssoCompleted");
          if (domainName) {
            if (loginReducer?.getSOConfiguration?.loginModule?.provider == "Auth0") {
              window.location.replace(logoutUrl);
            } else if (loginReducer?.getSOConfiguration?.loginModule?.provider == "OneLogin") { 
                if (searchParams.get("redirect_url")) {
                localStorage.setItem("redirectVia", "redirect_url")
                navigate(`/${subdirectory}?redirect_url=${searchParams.get("redirect_url")}`)
              }else{
                if (idToken && idToken !== null) {
                   window.location.replace(domainName + "/logout?post_logout_redirect_uri=" + logoutUrl + "&id_token_hint=" + idToken);
                } else {
                  if(window.location.href.includes(".jp")){
                    navigate("/")
                  }
                  else{
                    navigate("/"+ persistItemsReducer?.selectedSOData?.soCode?.toLowerCase());
                  }
                }
               }
            }
          } else {
            introspectToken();
          }
        }, 1000);
      }
    }
  }, [loginReducer?.getSOConfiguration?.loginModule?.provider]);
             
            
        


  /**
   * Retrieves an SSO token using Auth0, encrypts and stores it in localStorage if successful.
   * If unsuccessful, attempts to retrieve and set access and ID tokens from alternative sources.
   * Handles session expiration by displaying an alert and redirecting to the logout URL.
   *
   * @async
   * @function getSS0Token
   * @returns {Promise<void>} Resolves when the token retrieval and handling process is complete.
   */
  const getSS0Token = useCallback(async () => {
    try {
      let res = await getAuth0Token();
      handleTokenResponse(res);
    } catch (excep) {
      const logoutUrl = constants?.postLogoutUrl;
      if (excep && excep?.response?.data && excep?.response?.data?.error) {
        setTimeout(() => {
          removeAccessToken();
          removeIdToken();
          localStorage.removeItem("ssoCompleted");
          window.location.replace(logoutUrl);
        }, 1000);
      }
    }
  }, [persistItemsReducer?.selectedSOData.soCode]);

  // Code to handle SSO login
  useEffect(() => {
    const ssoCookie = getCookie(constants?.so_NZLO?.nzSSOCookieName);
    if (ssoCookie && !checkSSOStatus()) {
      getSS0Token().then(() => {
        try {
          const selectedSODetails = {
            soCode: "NZLO",
            languageCode: "13",
            soName: "New Zealand",
            soLanguageCultureCode: "en-NZ",
            shortLanguageCode: "en",
          };
          const response = getTokenResp();
          const decoded = response ? jwt_decode(response?.access_token) : "";
          let expirationTime = parseInt((DateUtilities.getCurrentTimeInMilliSec() + ((parseInt(response?.expires_in / 60)) * 60000)) / 1000);
          const emailValues = Object.keys(decoded).filter((propertyName) => (propertyName.includes("email") && !propertyName.includes("email_verified") && !propertyName.includes("emailverified")));
          let accessToken = response?.access_token;
          let sponsorID = decoded["wvnz-prod.au.auth0.com/supporterid"];
          const refreshToken = response?.refresh_token;
          let email = "";
          if (emailValues) {
            email = decoded[emailValues[0]];
          }
          const param = {
            ...persistItemsReducer,
            savedCountryList: persistItemsReducer?.loggedInCountryHistoryList,
            accessToken,
            selectedSOData: selectedSODetails,
            refreshToken,
            sponsorID,
            email,
            expirationTime,
            saveAuthParams: { ...persistItemsReducer?.saveAuthParams, id_token: idToken },
          };
          localStorage.setItem("ssoCompleted", "true");
          dispatch(PersistItemsAction.loginPersist(param));
          setActive(true);

        } catch (excep) {
          // Removed all console.log statements for excp and error
        }
      });
    }
  }, []);


  useEffect(() => {
    const ssoCookie = getCookie(constants?.so_NZLO?.nzSSOCookieName);
    if (ssoCookie && !checkSSOStatus()) {
      getSOConfigurations({ soCode: "NZLO", languageCode: "13" });
      return
    } else {
      if(!location?.origin?.includes(".nz") && location.pathname !== `/${params?.soname}` && persistItemsReducer?.selectedSOData?.soCode && !persistItemsReducer?.selectedSOData?.soCode?.toLowerCase().includes(params?.soname)){
        getSOConfigurations();
        setIsLoaded(true);       
       }else{
         !location?.origin?.includes(".nz") && location.pathname !== "/" && location.pathname !== `/${params?.soname}` && location.pathname !== `/mysponsorship/${params?.soname}` && introspectToken();
       }
    }
  }, [persistItemsReducer?.selectedSOData.soCode]);


  useEffect(() => {
    if (isLoaded) {
      const timeDiff = (DateUtilities.getMinutesDifferenceFromUnix(persistItemsReducer?.expirationTime) * -1);
      const accessTokenRenewIntervalInMinutes = loginReducer?.getSOConfiguration?.loginModule?.refreshTokenIntervalInMinutes
        ? (loginReducer?.getSOConfiguration?.loginModule?.refreshTokenIntervalInMinutes) : 10;
      const resetSession = ((timeDiff < accessTokenRenewIntervalInMinutes) && getRefreshToken());
      if (((searchParams.get("code") && searchParams.get("code") !== "")) || resetSession) {
        if (loginReducer?.getSOConfiguration?.loginModule?.domainName) {
          const code = searchParams.get("code") || "";
          let token_uri = loginReducer?.getSOConfiguration?.loginModule?.domainName.includes("https://") ? loginReducer?.getSOConfiguration?.loginModule?.domainName + "/token" : "https://" + loginReducer?.getSOConfiguration?.loginModule?.domainName + "/token";
          const refresh_token = getRefreshToken();
          const req_params =
            (resetSession) ?
              {
                client_id: loginReducer?.getSOConfiguration?.loginModule?.clientId,
                grant_type: "refresh_token",
                refresh_token: refresh_token
              } :
              {
                grant_type: "authorization_code",
                redirect_uri: constants?.currentOrigin,
                client_id: loginReducer?.getSOConfiguration?.loginModule?.clientId,
                code_verifier: "yqwe78wqeqw78eq7w6e127hj212kjhqwe",
                code
              }

          loginReducer?.getSOConfiguration?.loginModule?.provider && getToken(token_uri, req_params).then(() => {
            try {
              const response = getTokenResp();
              const decoded = (
                loginReducer?.getSOConfiguration?.loginModule?.provider === AppConstants.OneLogin) 
               ? jwt_decode(response?.id_token) : jwt_decode(response?.access_token);
              // let expirationTime = parseInt(decoded?.exp);
              let expirationTime = parseInt((DateUtilities.getCurrentTimeInMilliSec() + ((parseInt(response?.expires_in / 60)) * 60000)) / 1000);
              const propertyNames = Object.keys(decoded).filter((propertyName) =>
                propertyName.includes(loginReducer?.getSOConfiguration?.loginModule?.supporterID)
              );
              const emailValues = Object.keys(decoded).filter((propertyName) => (propertyName.includes("email") && !propertyName.includes("email_verified") && !propertyName.includes("emailverified")));
              let accessToken = response?.access_token;
              let sponsorID = (
                loginReducer?.getSOConfiguration?.loginModule?.provider === AppConstants.OneLogin) ?
                decoded[propertyNames[0]].SupporterID : decoded["wvnz-prod.au.auth0.com/supporterid"];
              const refreshToken = response?.refresh_token;
              let email = "";
              if (emailValues) {
                email = decoded[emailValues[0]];
              }
              const param = {
                ...persistItemsReducer,
                savedCountryList: persistItemsReducer?.loggedInCountryHistoryList,
                accessToken,
                refreshToken,
                sponsorID,
                email,
                expirationTime,
                saveAuthParams: { ...persistItemsReducer?.saveAuthParams, id_token: idToken },
              };
              // dispatch(PersistItemsAction.refreshPersist(response));
              dispatch(PersistItemsAction.loginPersist(param));
              setActive(true);
            } catch (excep) {
              // Removed all console.log statements for excp and error
            }
          });
        }
      } else {
        const accessToken = getAccessToken();
        const idToken = getIdToken();
        const refreshToken = getRefreshToken();
        if (accessToken && accessToken !== "") {
          const sponsorID = jwt_decode(accessToken)?.SupporterID ? jwt_decode(accessToken)?.SupporterID : jwt_decode(accessToken)["wvnz-prod.au.auth0.com/supporterid"];
          const email = jwt_decode(idToken).email;
          const param = {
            ...persistItemsReducer,
            savedCountryList: persistItemsReducer?.loggedInCountryHistoryList,
            accessToken,
            sponsorID,
            email,
            refreshToken,
            saveAuthParams: { ...persistItemsReducer?.saveAuthParams, id_token: idToken },
          };
          dispatch(PersistItemsAction.loginPersist(param));
          setActive(true);
        }
        introspectToken();
      }
      return persistItemsReducer;
    }
  }, [domainName, clientId, provider, refreshTokenIntervalInMinutes, supporterID, bearerTokenName]);


  const introspectToken = () => {
  const logoutUrl = (persistItemsReducer?.selectedSOData?.soCode === SELECTED_SO_CODE_JPNO) && location?.origin?.includes(".jp") ? 
    constants?.postLogoutUrl : 
    constants?.postLogoutUrl + persistItemsReducer?.selectedSOData?.soCode?.toLowerCase();    
    /*updated code- when user not logged in and deeplink url is clicked it should redirect to login screen*/
    if (!loginReducer?.getSOConfiguration?.loginModule?.domainName) {
      if (getAccessToken()) {
        return;
      } else {
        if (searchParams.get("redirect_url")) {
          localStorage.setItem("redirectVia", "redirect_url")
          navigate(`/${subdirectory}?redirect_url=${searchParams.get("redirect_url")}`)
        } else {
          if (searchParams.get("soname") && searchParams.get("soname") != "undefined") {
            localStorage.setItem("pathname", window.location.pathname)
            localStorage.setItem("search", window.location.search)
            localStorage.setItem("soname", searchParams.get("soname"))
            navigate(`/${searchParams.get("soname")}`)
          }
          else {
            navigate("/")
          }
        }
      }
    }
    const network = window.navigator.onLine;
    const accessToken = getAccessToken();
    const decoded = accessToken && jwt_decode(accessToken);

    if (network) {
      if (!searchParams.get("code") && accessToken && accessToken !== null && accessToken !== "") {
        if (loginReducer?.getSOConfiguration?.loginModule?.provider == "Auth0") {
          if (decoded && decoded?.exp) {
            const expirationTime = decoded?.exp;
            if (expirationTime) {
               const timeDiff = (DateUtilities.getMinutesDifferenceFromUnix(expirationTime) * -1);
              if (timeDiff <= 1) {
                setActive(false);
                const loginTimeout = 2000;
                (location.pathname !== "/" && location.pathname !== "/mysponsorship/" && location.pathname !== `/${params?.soname}` && location.pathname !== `/mysponsorship/${params?.soname}`) && setTimeout(() => {
                  let id_token = getIdToken();
                  removeAccessToken();
                  removeIdToken();
                  localStorage.removeItem("ssoCompleted");
                  dispatch(PersistItemsAction.logoutPersist(persistItemsReducer));
                  //added for invalid number fix
                  dispatch(PersistItemsAction.logoutReset());
                  if (id_token && id_token !== null) {
                    window.location.replace(logoutUrl);
                  } else {
                    // !active && 
                    navigate("/");
                  }
                }, loginTimeout)
              }
            }
          }
        }
        else {
          axios.post(loginReducer?.getSOConfiguration?.loginModule?.domainName + "/token/introspection",
            {
              token: accessToken,
              token_type_hint: "access_token",
              client_id: loginReducer?.getSOConfiguration?.loginModule?.clientId  //Austria
            },
            {
              headers: { "Content-Type": "application/x-www-form-urlencoded" }
            }).then((response) => {
              if (response.data.active == true) {
                 setActive(true)
               } else {
                setActive(false);
                const loginTimeout = 2000;
                (location.pathname !== "/" && location.pathname !== "/mysponsorship/" && location.pathname !== `/${params?.soname}` && location.pathname !== `/mysponsorship/${params?.soname}`) && setTimeout(() => {
                  let id_token = getIdToken();
                  removeAccessToken();
                  removeIdToken();
                  localStorage.removeItem("ssoCompleted");
                  dispatch(PersistItemsAction.logoutPersist(persistItemsReducer));
                  dispatch(PersistItemsAction.logoutReset());
                  if (id_token && id_token !== null) {
                    window.location.replace(loginReducer?.getSOConfiguration?.loginModule?.domainName + "/logout?post_logout_redirect_uri=" + logoutUrl + "&id_token_hint=" + id_token);
                  } else {
                    // !active && 
                    navigate("/");
                  }
                }, loginTimeout)
              }
            }).catch(() => {
              // Removed all console.log statements for excp and error
            })
        }
      } else {
        setTimeout(() => {
          removeAccessToken();
          localStorage.removeItem("ssoCompleted");
          if (window.location.pathname !== "/" && window.location.pathname !== "/mysponsorship" && window.location.pathname !== "/mysponsorship/" && location.pathname !== `/${params?.soname}` && location.pathname !== `/mysponsorship/${params?.soname}`) {
            //let pathname=window.location.pathname;
            let pathname = localStorage.getItem("pathname")
            pathname = pathname.replace("/mysponsorship", "")
            localStorage.setItem("redirectVia", "redirect_url")
            !active && navigate(`/${localStorage.getItem("soname")}?redirect_url=${pathname}${localStorage.getItem("search")}`)
            // !active && navigate(`/${subdirectory}?redirect_url=${pathname}${window.location.search}`)
          } else {
            if (searchParams.get("redirect_url")) {
              localStorage.setItem("redirectVia", "redirect_url")
              !active && navigate(`/${subdirectory}?redirect_url=${searchParams.get("redirect_url")}`)
            } else {
              !active && navigate("/")
            }

          }

        }, 500);
      }
    }
    else {
      setAlert(t("Warning"), t("NoInternet"), "", "", "error", false, false, true)
    }

    return active;
  }

  return [active, accessToken, getAccessToken];

}

export default useLogin;